import {LiveAnnouncer} from '@angular/cdk/a11y';
import {Component, EventEmitter, Input, OnInit, Output, ViewChild} from '@angular/core';
import {NgForm} from '@angular/forms';
import {LoaderService, ToastService} from '@core/src/public-api';
import {LoginModel, LoginResendEmail} from '@shared/models/login.model';
import {AppConfigService} from '@shared/services/app-config.service';
import {LoginService} from '@shared/services/login.service';
import {PermissionService} from '@shared/services/permission.service';
import {UserService} from '@shared/services/user.service';

@Component({
  selector: 'si-login-dialog',
  templateUrl: './login-dialog.component.html',
  styleUrls: ['./login-dialog.component.scss']
})
export class LoginDialogComponent implements OnInit {
  @ViewChild('requestLoginForm') form: NgForm;
  @Input() displayDialog: boolean;
  @Output() displayDialogChange = new EventEmitter<boolean>();
  @Output() loginSuccessChange = new EventEmitter<boolean>();
  model = new LoginModel();
  errorText: string;
  tooltipTextPasswordPolicies: string;
  displayResendVerifyEmailDialog: boolean;
  displaySMSVerificationDialog: boolean;
  displayEmailVerificationDialog: boolean;
  verificationErrorText: string;
  phoneValue: string;

  constructor(
    private loginService: LoginService,
    private appConfigService: AppConfigService,
    private permissionService: PermissionService,
    private userService: UserService,
    private liveAnnouncer: LiveAnnouncer,
    private loaderService: LoaderService,
    private toastService: ToastService
  ) { }

  ngOnInit(): void {
    this.getPasswordPolicies();
  }

  async getPasswordPolicies(): Promise<void> {
    this.tooltipTextPasswordPolicies = await this.loginService.getPasswordPoliciesText();
  }

  async onSubmit(): Promise<void> {
    if (this.form.invalid) {
      return;
    }

    await this.login();
  }

  async login(): Promise<void> {
    this.errorText = null;
    try {
      this.loaderService.start();
      await this.loginService.login(this.model.username, this.model.password, this.model.code);
      await this.appConfigService.getConfigurations();
      await this.permissionService.getPermissions();
      this.userService.setInactivityFlag(false);
      this.loginSuccessChange.emit();
      this.onClickCloseDialog();
    } catch (e) {
      if (e.error?.applicationCode === 'PCS0010') {
        this.displayDialog = false;
        this.displayResendVerifyEmailDialog = true;
        return;
      }

      if (e.error?.applicationCode === 'PCS0052') {
        this.displayDialog = false;
        this.displaySMSVerificationDialog = true;
        this.phoneValue = e.error?.message;
        return;
      }

      if (e.error?.applicationCode === 'PCS0053') {
        this.displayDialog = false;
        this.displayEmailVerificationDialog = true;
        return;
      }

      if (e.error?.applicationCode === 'PCS0054' || e.error?.applicationCode === 'PCS0056') {
        this.displaySMSVerificationDialog = false;
        this.displayEmailVerificationDialog = false;
        this.onChangeDisplayVerificationDialog(false);
      }

      if (e.error?.applicationCode === 'PCS0055') {
        this.verificationErrorText = e.error?.message;
        this.liveAnnouncer.announce(e.error.message, 'assertive');
        return;
      }

      this.errorText = e.error.message;
      this.liveAnnouncer.announce(e.error.message, 'assertive');
    } finally {
      this.loaderService.stop();
    }
  }

  onClickCloseDialog(): void {
    this.displayDialog = false;
    this.displayDialogChange.emit(this.displayDialog);
  }

  onChangeDisplayVerificationDialog(displayDialog: boolean): void {
    if (!displayDialog) {
      this.model.code = null;
      this.verificationErrorText = null;
      this.phoneValue = null;
      this.displayDialog = true;
      this.displayDialogChange.emit(true);
    }
  }

  onChangeVerificationCode(): void {
    this.login();
  }

  async onClickButtonResendVerifyEmailDialog(action: boolean): Promise<void> {
    if (action) {
      await this.setResendVerifyEmail();
    }

    this.onClickCloseDialog();
  }

  async setResendVerifyEmail(): Promise<void> {
    const resendEmailModel = {email: this.model.username} as LoginResendEmail;
    await this.loginService.setResendValidationEmail(resendEmailModel);
    this.toastService.success('SI_RESEND_VERIFY_EMAIL_SUCCESS');
  }
}
